/**
 * AnkaufChecklistModal — Jun-2026 (User-Wunsch, überarbeitet).
 * Erscheint im Ankauf-/Inzahlungnahme-Flow nach „JA" und VOR dem ZB1-Scan.
 *
 * Je Position „vorhanden / nicht vorhanden". Bei „nicht vorhanden" klappen
 * Detail-Optionen auf; fehlt ein Beleg/Schlüssel endgültig (verloren / nicht
 * vorhanden), wird der Bruttopreis aus der Warenwirtschaft (Kategorie
 * „GW-Preisliste") als Abzug hinterlegt und bei der KI-/DAT-Preisermittlung
 * berücksichtigt. Die Antworten gehen in den Ankaufsvertrag + die Dispo-Felder.
 */
import { useState, useEffect } from "react";
import { ClipboardCheck, AlertTriangle, Info, FileText, Wrench, KeyRound, ChevronDown } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { DispoBackButton } from "./DispoBackButton";
import { FeatureGuideTriangle } from "./FeatureGuideTriangle";
import DamageChecklist from "../../leasing/DamageChecklist";

const fmtEur = (n) => `${Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// Standard-Bruttopreise (GW-Preisliste) — spiegeln die Backend-Defaults.
// Maßgeblich bleibt der in der Warenwirtschaft hinterlegte Preis (vom Backend).
const PRICES = {
    "PAPIERE-COC-ABE": 189,
    "PAPIERE-ZB1-ZULASSUNG": 229,
    "PAPIERE-ZB2-KRAFTFAHRZEUGBRIEF": 369,
    "GW-ABMELDUNG": 229,
    "SCHLÜSSEL-ZÜNDUNG": 175,
    "SCHLÜSSEL-WEGFAHRSPERRE": 889,
    "SCHLÜSSEL-FERNBEDIENUNG": 889,
};

// Zwei „O"-Radios nebeneinander (vorhanden / nicht vorhanden o. ä.).
function YesNo({ name, value, onChange, yesLabel = "Vorhanden", noLabel = "Nicht vorhanden", testid }) {
    return (
        <div className="flex flex-wrap gap-x-5 gap-y-1" data-testid={testid}>
            <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                <input type="radio" name={name} checked={value === "yes"} onChange={() => onChange("yes")} className="accent-emerald-600" data-testid={`${testid}-yes`} />
                {yesLabel}
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                <input type="radio" name={name} checked={value === "no"} onChange={() => onChange("no")} className="accent-red-600" data-testid={`${testid}-no`} />
                {noLabel}
            </label>
        </div>
    );
}

// Aufklapp-Detailauswahl (zwei Gründe), eine davon löst ggf. einen Preis aus.
function ReasonChoice({ name, value, onChange, options, testid }) {
    return (
        <div className="pl-5 mt-1.5 flex flex-col gap-1 border-l-2 border-gray-200" data-testid={testid}>
            {options.map((o) => (
                <label key={o.v} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer pl-2">
                    <input type="radio" name={name} checked={value === o.v} onChange={() => onChange(o.v)} data-testid={`${testid}-${o.v}`} />
                    {o.l}
                </label>
            ))}
        </div>
    );
}

// Amber-Hinweisbox mit Text + Bruttopreis (GW-Preisliste).
function PriceHint({ sku, text, testid }) {
    return (
        <div className="ml-7 mt-1.5 flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[12px] text-amber-900" data-testid={testid}>
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
            <span className="flex-1">{text}</span>
            <span className="font-bold font-mono-num whitespace-nowrap">{fmtEur(PRICES[sku])}</span>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="space-y-1.5 border-b border-gray-100 pb-3">
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">{title}</Label>
            {children}
        </div>
    );
}

// Jul-2026 (User-Wunsch) — Freitextfeld + Betragseingabe am unteren Ende jedes
// Schadensermittlung-Tabs. Der Betrag fließt als zusätzlicher Abzug in die
// Preisermittlung ein.
function CustomPosition({ note, amount, onNote, onAmount, prefix }) {
    return (
        <Section title="Weitere Position (Freitext + Betrag)">
            <div className="flex flex-col sm:flex-row gap-2 sm:items-start">
                <textarea
                    value={note || ""}
                    onChange={(e) => onNote(e.target.value)}
                    rows={2}
                    placeholder="Freitext: zusätzliche Schadensposition / Bemerkung …"
                    className="flex-1 w-full text-sm border border-gray-300 rounded-sm px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#111827]"
                    data-testid={`${prefix}-custom-note`}
                />
                <div className="flex items-center gap-1 shrink-0">
                    <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={amount ?? ""}
                        onChange={(e) => onAmount(e.target.value)}
                        placeholder="0,00"
                        className="rounded-sm w-28 h-9 font-mono-num"
                        data-testid={`${prefix}-custom-amount`}
                    />
                    <span className="text-sm text-gray-500">€</span>
                </div>
            </div>
            <p className="text-[10px] text-gray-400">Fließt als zusätzlicher Abzug in die Preisermittlung ein.</p>
        </Section>
    );
}


export default function AnkaufChecklistModal({ onBack, onClose, onSubmit, vehicle = {}, submitLabel = "Weiter zum ZB1-Scan", initialCl = null, initialTechnik = null, title = "Ankauf-Checkliste", inline = false, onChange = null, startOpen = false }) {
    const [tab, setTab] = useState("docs"); // docs = Unterlagen · keys = Schlüssel · technik = Technik/Optik
    const [openInline, setOpenInline] = useState(startOpen);
    // Jul-2026 (User-Wunsch) — der Nutzer muss alle 3 Tabs durcharbeiten, bevor er
    // weiterkommt. „visited" merkt sich die bereits geöffneten Tabs (persistiert).
    const [visited, setVisited] = useState({ docs: true, ...(initialCl?.visited_tabs || {}) });
    const [hint, setHint] = useState("");
    const goTab = (t) => { setTab(t); setVisited((p) => ({ ...p, [t]: true })); setHint(""); };
    const allVisited = visited.docs && visited.keys && visited.technik;
    // Jul-2026 (User-Wunsch) — Technik/Optik-Schäden aus dem gemeinsamen GW-Katalog
    // (identisch mit Leasing-Rückgabe). Antworten + Brutto-Schätzung fließen mit ein.
    const [technikAnswers, setTechnikAnswers] = useState(initialTechnik?.answers || {});
    const [technikEstimate, setTechnikEstimate] = useState(initialTechnik?.estimate || null);
    // Jul-2026 (User-Wunsch) — Auswahlfeld: sollen die erfassten Technik/Optik-Abzüge
    // als Positionen in den Ankaufvertrag / das Preisangebot übernommen werden?
    // Standard: NICHT angehakt.
    const [includeInAnkauf, setIncludeInAnkauf] = useState(!!initialTechnik?.include_in_ankauf);
    const [cl, setCl] = useState(initialCl || {
        coc_abe: "", coc_abe_reason: "",
        zb1: "", zb1_reason: "",
        zb2: "", zb2_reason: "",
        registration: "", registration_reason: "",
        key_count: "",
        ignition_key: "", immobilizer: "", immobilizer_reason: "",
        keyless: "", keyless_reason: "",
        remote: "", remote_reason: "",
        extra_keys_accessories: false,
        // Jul-2026 (User-Wunsch) — je Tab ein Freitextfeld + Betrag (zusätzlicher Abzug).
        docs_custom_note: "", docs_custom_amount: "",
        keys_custom_note: "", keys_custom_amount: "",
        technik_custom_note: "", technik_custom_amount: "",
    });
    const set = (k, v) => setCl((p) => ({ ...p, [k]: v }));
    // „Nicht vorhanden" → Detailgrund zurücksetzen, wenn wieder „vorhanden".
    const setYN = (field, reasonField) => (v) => setCl((p) => ({ ...p, [field]: v, ...(v === "yes" && reasonField ? { [reasonField]: "" } : {}) }));

    // Live-Abzüge (Anzeige; maßgeblich ist der Backend-Wert aus der GW-Preisliste).
    const deductions = [];
    const add = (sku) => deductions.push({ sku, gross: PRICES[sku] });
    if (cl.coc_abe === "no" && cl.coc_abe_reason === "lost") add("PAPIERE-COC-ABE");
    if (cl.zb1 === "no" && cl.zb1_reason === "lost") add("PAPIERE-ZB1-ZULASSUNG");
    if (cl.zb2 === "no" && cl.zb2_reason === "lost") add("PAPIERE-ZB2-KRAFTFAHRZEUGBRIEF");
    if (cl.registration === "registered") add("GW-ABMELDUNG");
    if (cl.ignition_key === "no") add("SCHLÜSSEL-ZÜNDUNG");
    if (cl.immobilizer === "no" && cl.immobilizer_reason === "missing") add("SCHLÜSSEL-WEGFAHRSPERRE");
    if (cl.keyless === "no" && cl.keyless_reason === "missing" && cl.immobilizer_reason !== "missing") add("SCHLÜSSEL-WEGFAHRSPERRE");
    if (cl.remote === "no" && cl.remote_reason === "lost") add("SCHLÜSSEL-FERNBEDIENUNG");
    const dedTotal = deductions.reduce((s, d) => s + d.gross, 0);

    const needsDeregProof = cl.registration === "not_needed" && cl.registration_reason === "customer_deregistered";

    const submit = () => {
        if (!allVisited) {
            const missing = [!visited.docs && "Unterlagen", !visited.keys && "Schlüssel", !visited.technik && "Technik / Optik"].filter(Boolean);
            const next = !visited.docs ? "docs" : !visited.keys ? "keys" : "technik";
            setTab(next);
            setVisited((p) => ({ ...p, [next]: true }));
            setHint(`Bitte alle Bereiche durcharbeiten. Noch offen: ${missing.join(", ")}.`);
            return;
        }
        onSubmit({
            ...cl,
            key_count: cl.key_count === "" ? null : Number(cl.key_count),
            visited_tabs: visited,
            complete: allVisited,
            technik_optik: { answers: technikAnswers, estimate: technikEstimate, include_in_ankauf: includeInAnkauf },
            deduction_docs_total: dedTotal + (Number(cl.docs_custom_amount) || 0) + (Number(cl.keys_custom_amount) || 0),
            deduction_total: dedTotal + Number(technikEstimate?.total || 0) + customAmounts,
            include_in_ankauf: includeInAnkauf,
        });
    };
    const technikTotal = Number(technikEstimate?.total || 0);
    // Jul-2026 (User-Wunsch) — Freitext-Beträge je Tab fließen als zusätzliche Abzüge ein.
    const customAmounts = [cl.docs_custom_amount, cl.keys_custom_amount, cl.technik_custom_amount]
        .reduce((s, v) => s + (Number(v) || 0), 0);
    const combinedTotal = dedTotal + technikTotal + customAmounts;

    // Inline-Modus (Inserieren): Änderungen live nach oben spiegeln (kein „Übernehmen").
    const buildData = () => ({
        ...cl,
        key_count: cl.key_count === "" ? null : Number(cl.key_count),
        visited_tabs: visited,
        complete: allVisited,
        technik_optik: { answers: technikAnswers, estimate: technikEstimate, include_in_ankauf: includeInAnkauf },
        deduction_docs_total: dedTotal + (Number(cl.docs_custom_amount) || 0) + (Number(cl.keys_custom_amount) || 0),
        deduction_total: dedTotal + Number(technikEstimate?.total || 0) + customAmounts,
        include_in_ankauf: includeInAnkauf,
    });
    useEffect(() => {
        if (inline && onChange) onChange(buildData());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cl, technikAnswers, technikEstimate, includeInAnkauf, visited]);

    // Jul-2026 (User-Wunsch) — von außen (z. B. Hinweis in „Einstand & Kalkulation")
    // aufklappbar: CustomEvent „open-condition-assessment" öffnet den Inline-Block.
    useEffect(() => {
        if (!inline) return undefined;
        const h = () => setOpenInline(true);
        window.addEventListener("open-condition-assessment", h);
        return () => window.removeEventListener("open-condition-assessment", h);
    }, [inline]);

    const content = (
        <>
                <p className="text-xs text-gray-500 -mt-1">Diese Angaben werden in den Ankaufsvertrag und die Fahrzeug-Disposition übernommen. Fehlende Belege/Schlüssel werden mit dem Bruttopreis aus der Warenwirtschaft (GW-Preisliste) bei der Preisermittlung berücksichtigt.</p>

                {/* Jul-2026 (User-Wunsch) — 3 Bereiche: Unterlagen · Schlüssel · Technik/Optik. Alle müssen durchlaufen werden. */}
                <div className="flex items-center gap-1 border-b mt-2" data-testid="ankauf-tabs">
                    <button type="button" onClick={() => goTab("docs")} className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-bold rounded-t-sm ${tab === "docs" ? "bg-[#111827] text-[#FFCC00]" : "text-gray-600 hover:bg-gray-100"}`} data-testid="ankauf-tab-docs">
                        <FileText className="w-4 h-4" /> Unterlagen {visited.docs && <span className="text-emerald-500" data-testid="ankauf-tab-docs-done">✓</span>}
                    </button>
                    <button type="button" onClick={() => goTab("keys")} className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-bold rounded-t-sm ${tab === "keys" ? "bg-[#111827] text-[#FFCC00]" : "text-gray-600 hover:bg-gray-100"}`} data-testid="ankauf-tab-keys">
                        <KeyRound className="w-4 h-4" /> Schlüssel {visited.keys && <span className="text-emerald-500" data-testid="ankauf-tab-keys-done">✓</span>}
                    </button>
                    <button type="button" onClick={() => goTab("technik")} className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-bold rounded-t-sm ${tab === "technik" ? "bg-[#111827] text-[#FFCC00]" : "text-gray-600 hover:bg-gray-100"}`} data-testid="ankauf-tab-technik">
                        <Wrench className="w-4 h-4" /> Technik / Optik {visited.technik && <span className="text-emerald-500" data-testid="ankauf-tab-technik-done">✓</span>}
                    </button>
                </div>
                {hint && (
                    <div className="mt-2 flex items-start gap-2 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900" data-testid="ankauf-tabs-hint">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" /><span>{hint}</span>
                    </div>
                )}

                <div className={`space-y-3 mt-1 ${tab === "docs" ? "" : "hidden"}`}>
                    {/* COC / ABE */}
                    <Section title="COC (Konformitätserklärung) / ABE (Allgemeine Betriebserlaubnis)">
                        <YesNo name="coc" value={cl.coc_abe} onChange={setYN("coc_abe", "coc_abe_reason")} testid="ankauf-coc" />
                        {cl.coc_abe === "no" && (
                            <>
                                <ReasonChoice name="coc-reason" value={cl.coc_abe_reason} onChange={(v) => set("coc_abe_reason", v)} testid="ankauf-coc-reason"
                                    options={[{ v: "has_zb2", l: "Fahrzeug hat einen ZB2 (Fahrzeugbrief)" }, { v: "lost", l: "verloren gegangen" }]} />
                                {cl.coc_abe_reason === "lost" && <PriceHint sku="PAPIERE-COC-ABE" text="COC oder ABE fehlt — muss neu aufgeboten werden" testid="ankauf-coc-price" />}
                            </>
                        )}
                    </Section>

                    {/* ZB1 */}
                    <Section title="ZB1 (Zulassung)">
                        <YesNo name="zb1" value={cl.zb1} onChange={setYN("zb1", "zb1_reason")} testid="ankauf-zb1" />
                        {cl.zb1 === "no" && (
                            <>
                                <ReasonChoice name="zb1-reason" value={cl.zb1_reason} onChange={(v) => set("zb1_reason", v)} testid="ankauf-zb1-reason"
                                    options={[{ v: "not_required", l: "Nicht zulassungspflichtig (bis 50 ccm)" }, { v: "lost", l: "verloren gegangen" }]} />
                                {cl.zb1_reason === "lost" && <PriceHint sku="PAPIERE-ZB1-ZULASSUNG" text="ZB1 (Zulassung) fehlt — muss neu aufgeboten werden" testid="ankauf-zb1-price" />}
                            </>
                        )}
                    </Section>

                    {/* ZB2 */}
                    <Section title="ZB2 (Fahrzeugbrief)">
                        <YesNo name="zb2" value={cl.zb2} onChange={setYN("zb2", "zb2_reason")} testid="ankauf-zb2" />
                        {cl.zb2 === "no" && (
                            <>
                                <ReasonChoice name="zb2-reason" value={cl.zb2_reason} onChange={(v) => set("zb2_reason", v)} testid="ankauf-zb2-reason"
                                    options={[{ v: "has_coc", l: "Fahrzeug hat COC (bis 125 ccm)" }, { v: "lost", l: "verloren gegangen" }]} />
                                {cl.zb2_reason === "lost" && <PriceHint sku="PAPIERE-ZB2-KRAFTFAHRZEUGBRIEF" text="ZB2 (Kraftfahrzeugbrief) fehlt — muss neu aufgeboten werden" testid="ankauf-zb2-price" />}
                            </>
                        )}
                    </Section>

                    {/* Zulassungsstatus */}
                    <Section title="Zulassungsstatus">
                        <div className="flex flex-col gap-1" data-testid="ankauf-registration">
                            <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                                <input type="radio" name="reg" checked={cl.registration === "registered"} onChange={() => set("registration", "registered")} data-testid="ankauf-registration-registered" />
                                Fahrzeug ist noch zugelassen (aktuell zugelassen)
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                                <input type="radio" name="reg" checked={cl.registration === "not_needed"} onChange={() => set("registration", "not_needed")} data-testid="ankauf-registration-not-needed" />
                                Fahrzeug muss nicht abgemeldet werden
                            </label>
                        </div>
                        {cl.registration === "registered" && <PriceHint sku="GW-ABMELDUNG" text="Abmeldekosten inkl. Service" testid="ankauf-registration-price" />}
                        {cl.registration === "not_needed" && (
                            <ReasonChoice name="reg-reason" value={cl.registration_reason} onChange={(v) => set("registration_reason", v)} testid="ankauf-registration-reason"
                                options={[{ v: "exempt", l: "nicht zulassungspflichtig" }, { v: "customer_deregistered", l: "Kunde hat abgemeldet" }]} />
                        )}
                        {needsDeregProof && (
                            <div className="ml-7 mt-1.5 flex items-start gap-2 rounded-sm border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-[12px] text-sky-900" data-testid="ankauf-registration-proof-hint">
                                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-sky-600" />
                                <span>Bitte vom Kunden den <strong>Abmeldenachweis</strong> anfordern!</span>
                            </div>
                        )}
                    </Section>
                    <CustomPosition prefix="ankauf-docs" note={cl.docs_custom_note} amount={cl.docs_custom_amount}
                        onNote={(v) => set("docs_custom_note", v)} onAmount={(v) => set("docs_custom_amount", v)} />
                </div>

                {/* Tab „Schlüssel" — aus dem Unterlagen-Tab ausgegliedert (Jul-2026, User-Wunsch). */}
                <div className={`space-y-3 mt-1 ${tab === "keys" ? "" : "hidden"}`} data-testid="ankauf-keys">
                    {/* Schlüssel */}
                    <Section title="Schlüssel & Zugang">
                        <div className="flex items-center gap-2">
                            <Label className="text-xs text-gray-600">Anzahl Schlüssel gesamt</Label>
                            <Input type="number" min="0" value={cl.key_count} onChange={(e) => set("key_count", e.target.value)} className="rounded-sm w-24 h-8" data-testid="ankauf-key-count" />
                        </div>

                        <div className="mt-1.5">
                            <span className="text-[13px] font-semibold text-gray-700">Zündschlüssel</span>
                            <YesNo name="ign" value={cl.ignition_key} onChange={(v) => set("ignition_key", v)} testid="ankauf-ignition" />
                            {cl.ignition_key === "no" && <PriceHint sku="SCHLÜSSEL-ZÜNDUNG" text="Schließzylinder und Schlüsselsatz müssen getauscht werden" testid="ankauf-ignition-price" />}
                        </div>

                        <div className="mt-1.5">
                            <span className="text-[13px] font-semibold text-gray-700">Wegfahrsperren / Masterschlüssel</span>
                            <YesNo name="imm" value={cl.immobilizer} onChange={setYN("immobilizer", "immobilizer_reason")} testid="ankauf-immobilizer" />
                            {cl.immobilizer === "no" && (
                                <>
                                    <ReasonChoice name="imm-reason" value={cl.immobilizer_reason} onChange={(v) => set("immobilizer_reason", v)} testid="ankauf-immobilizer-reason"
                                        options={[{ v: "none", l: "Fahrzeug hat keine Wegfahrsperre" }, { v: "missing", l: "nicht vorhanden" }]} />
                                    {cl.immobilizer_reason === "missing" && <PriceHint sku="SCHLÜSSEL-WEGFAHRSPERRE" text="Steuergerät muss für die Wegfahrsperre getauscht werden" testid="ankauf-immobilizer-price" />}
                                </>
                            )}
                        </div>

                        <div className="mt-1.5">
                            <span className="text-[13px] font-semibold text-gray-700">KeylessGo Sicherheitskarte / Transponder</span>
                            <YesNo name="keyless" value={cl.keyless} onChange={setYN("keyless", "keyless_reason")} testid="ankauf-keyless" />
                            {cl.keyless === "no" && (
                                <>
                                    <ReasonChoice name="keyless-reason" value={cl.keyless_reason} onChange={(v) => set("keyless_reason", v)} testid="ankauf-keyless-reason"
                                        options={[{ v: "none", l: "nicht serienmäßig" }, { v: "missing", l: "nicht vorhanden" }]} />
                                    {cl.keyless_reason === "missing" && <PriceHint sku="SCHLÜSSEL-WEGFAHRSPERRE" text="Steuergerät muss für die Wegfahrsperre getauscht werden" testid="ankauf-keyless-price" />}
                                </>
                            )}
                        </div>

                        <div className="mt-1.5">
                            <span className="text-[13px] font-semibold text-gray-700">Fernbedienung</span>
                            <YesNo name="remote" value={cl.remote} onChange={setYN("remote", "remote_reason")} testid="ankauf-remote" />
                            {cl.remote === "no" && (
                                <>
                                    <ReasonChoice name="remote-reason" value={cl.remote_reason} onChange={(v) => set("remote_reason", v)} testid="ankauf-remote-reason"
                                        options={[{ v: "not_standard", l: "nicht serienmäßig" }, { v: "lost", l: "verloren gegangen" }]} />
                                    {cl.remote_reason === "lost" && <PriceHint sku="SCHLÜSSEL-FERNBEDIENUNG" text="Fernbedienung ersetzen inkl. Montage" testid="ankauf-remote-price" />}
                                </>
                            )}
                        </div>

                        <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer mt-2">
                            <input type="checkbox" checked={cl.extra_keys_accessories} onChange={(e) => set("extra_keys_accessories", e.target.checked)} className="accent-[#111827]" data-testid="ankauf-extra-keys" />
                            weiteres Schlüssel-Zubehör, Topcase, Schlösser abgegeben
                        </label>
                        {cl.extra_keys_accessories && (
                            <textarea
                                value={cl.extra_keys_accessories_note || ""}
                                onChange={(e) => set("extra_keys_accessories_note", e.target.value)}
                                placeholder="Welches Zubehör wurde abgegeben? (z. B. Topcase, 2. Schlüssel, Bügelschloss …)"
                                rows={2}
                                className="mt-1.5 w-full text-sm border border-gray-300 rounded-sm px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#111827]"
                                data-testid="ankauf-extra-keys-note"
                            />
                        )}
                    </Section>
                    <CustomPosition prefix="ankauf-keys" note={cl.keys_custom_note} amount={cl.keys_custom_amount}
                        onNote={(v) => set("keys_custom_note", v)} onAmount={(v) => set("keys_custom_amount", v)} />
                </div>

                {/* Tab 2 — Technik / Optik: gemeinsamer GW-Katalog (identisch mit Leasing-Rückgabe). */}
                <div className={`mt-1 ${tab === "technik" ? "" : "hidden"}`} data-testid="ankauf-technik-optik">
                    <DamageChecklist
                        vehicle={vehicle}
                        answers={technikAnswers}
                        context="ankauf"
                        onChange={(ans, est) => { setTechnikAnswers(ans); setTechnikEstimate(est); }}
                    />
                    <label className="mt-3 flex items-start gap-2 text-sm text-gray-800 cursor-pointer rounded-sm border border-gray-200 bg-gray-50 px-3 py-2" data-testid="ankauf-include-checkbox">
                        <input type="checkbox" checked={includeInAnkauf} onChange={(e) => setIncludeInAnkauf(e.target.checked)} className="mt-0.5 accent-[#111827]" data-testid="ankauf-include-checkbox-input" />
                        <span>
                            <span className="font-semibold">Diese Abzüge in den Ankaufvertrag / das Preisangebot übernehmen</span>
                            <span className="block text-xs text-gray-500">Die Technik/Optik-Positionen werden als Abzüge im Vertrag aufgeführt, damit der Endpreis transparent hergeleitet ist. (Standard: aus)</span>
                        </span>
                    </label>
                    <div className="mt-3">
                        <CustomPosition prefix="ankauf-technik" note={cl.technik_custom_note} amount={cl.technik_custom_amount}
                            onNote={(v) => set("technik_custom_note", v)} onAmount={(v) => set("technik_custom_amount", v)} />
                    </div>
                </div>

                {/* Abzugs-Summe (Unterlagen/Schlüssel + Technik/Optik) */}
                <div className={`rounded-sm border-2 px-3 py-2 flex items-center justify-between ${combinedTotal > 0 ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-gray-50"}`} data-testid="ankauf-deductions-summary">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Abzüge (GW-Preisliste) — fließen in die KI-/DAT-Preisermittlung</span>
                    <span className={`font-mono-num font-black ${combinedTotal > 0 ? "text-amber-700" : "text-gray-500"}`} data-testid="ankauf-deductions-total">
                        {combinedTotal > 0 ? `− ${fmtEur(combinedTotal)}` : "0 €"}
                    </span>
                </div>
        </>
    );

    // Inline-Modus (Inserieren): auf-/einklappbare Karte statt Dialog — kein „Übernehmen".
    if (inline) {
        return (
            <div data-testid="listing-schadensermittlung">
                <button
                    type="button"
                    onClick={() => setOpenInline((o) => !o)}
                    className="w-full flex items-center gap-3 px-6 py-4 bg-white border border-gray-200 rounded-sm hover:bg-gray-50 transition-colors text-left"
                    aria-expanded={openInline}
                    data-testid="open-schadensermittlung-btn"
                >
                    <div className="w-9 h-9 bg-[#FFCC00] flex items-center justify-center rounded-sm shrink-0">
                        <ClipboardCheck className="w-4 h-4 text-[#111827]" strokeWidth={2.5} />
                    </div>
                    <span className="flex-1 min-w-0">
                        <span className="block text-sm font-bold text-[#111827]">{title}</span>
                        <span className="block text-xs text-gray-500">Unterlagen/Schlüssel + Technik/Optik (GW-Preisliste) — identisch mit Ankauf &amp; Leasing.</span>
                    </span>
                    {!allVisited && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-300 rounded-sm px-1.5 py-0.5 shrink-0" data-testid="listing-schaden-incomplete-badge">
                            Pflicht · noch offen: {[!visited.docs && "Unterlagen", !visited.keys && "Schlüssel", !visited.technik && "Technik/Optik"].filter(Boolean).join(", ")}
                        </span>
                    )}
                    {allVisited && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-sm px-1.5 py-0.5 shrink-0" data-testid="listing-schaden-complete-badge">✓ durchgeführt</span>
                    )}
                    {combinedTotal > 0 ? (
                        <span className="text-xs font-bold text-amber-700 font-mono-num shrink-0" data-testid="listing-schaden-total">− {fmtEur(combinedTotal)}</span>
                    ) : (
                        <span className="text-[11px] font-bold text-gray-400 shrink-0" data-testid="listing-schaden-total">noch nicht ermittelt</span>
                    )}
                    <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform shrink-0 ${openInline ? "rotate-180" : ""}`} />
                </button>
                {openInline && (
                    <div className="px-6 py-4 bg-white border border-t-0 border-gray-200 rounded-sm -mt-2 space-y-3" data-testid="listing-schadensermittlung-content">
                        {content}
                    </div>
                )}
            </div>
        );
    }

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="w-[96vw] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ankauf-checklist-modal">
                <FeatureGuideTriangle guideKey="ankauf" />
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-[#111827]" /> {title}</DialogTitle>
                </DialogHeader>
                {content}
                <DialogFooter>
                    {onBack && <DispoBackButton onBack={onBack} />}
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    <Button onClick={submit} className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold" data-testid="ankauf-checklist-continue">
                        {submitLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
